import Modal, { ModalSize } from "~/common/components/Modal";
import { type ReactNode, useMemo, useState } from "react";
import { useAddToCartWizardContext } from "~/features/addToCart/components/AddToCartProcess/AddToCartWizard";
import AtcProductInfo from "~/features/addToCart/components/v2/AtcProductInfo/AtcProductInfo";
import { useFormContext } from "react-hook-form";
import { type AddToCartFormData } from "~/features/addToCart/hooks/useAddToCartNew";
import OptimeSolutionsMessage from "~/domains/optime/components/OptimeSolutionsMessage";
import { getCountriesListFromFifteendigit, isProductPMA } from "~/domains/product/utils/productDescriptorUtils";
import { Permission, userHasAnyPermission, userHasPermission } from "~/domains/user/utils/permissions";
import DisclaimerCountryOrigin from "~/domains/product/components/DisclaimerCountryOrigin";
import { type HousingProduct, HousingProductType, isKITProduct } from "~/domains/product/types/ProductData";
import classNames from "classnames";
import ProductDesignation from "~/features/productDetails/components/ProductDesignation";
import { Trans, useTranslation } from "react-i18next";
import { Translation } from "~/app/i18n/Translation";
import { HousingMessages } from "~/domains/housing/components/HousingMessages";
import { useIsMinDesktopSize, useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { type ColumnDef } from "@tanstack/react-table";
import { TanStackTable } from "~/common/components/Table/TanStackTable";
import { InfoProductMessagePMA } from "~/features/productDetails/components/ProductSummary/InfoProductMessagePMA";
import { LinkButton } from "~/common/components/Link";
import { Stack } from "~/common/mui";
import { isNonNull } from "~/common/utils/reducers";
import { Alert } from "~/common/mui/Alert/Alert";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import AtcProductPriceInfo from "~/features/addToCart/components/v2/AtcProductPriceInfo/AtcProductPriceInfo";
import { isSpecialPrice } from "~/features/productDetails/redux/productDetailsSelectors";

interface Props {
  productDescriptor: ProductDescriptor;
  submit: () => void;
}

export const KITSummaryModal = ({ productDescriptor, submit }: Props) => {
  const { closePopup, getMainCls, t, getCloseBtn, getModalProps, getActionBtn, editMode } = useAddToCartWizardContext();

  const formProps = useFormContext<AddToCartFormData>();
  const watch = formProps.watch;
  const setValue = formProps.setValue;

  const quantity = watch("quantity");
  const priceReference = watch("priceReference");
  const price = watch("netPrice");
  const priceUnit = watch("priceUnit");
  const selectedPackCode = watch("selectedPackCode");

  const optionalPartsForm = watch("optionalParts");

  const isTablet = useIsMinTabletSize();
  const isDesktop = useIsMinDesktopSize();

  if (!isKITProduct(productDescriptor.base)) {
    return null;
  }

  const { includedParts, optionalParts } = productDescriptor.base;
  const hasOptionalParts = Object.values(optionalPartsForm).some((part) => part.selected);
  const hasIncludedParts = !!includedParts.length;

  const selectedPack = watch("selectedPack");
  const isPackageSizeInvalid = quantity === 0 || (selectedPack && !Number.isInteger(quantity / selectedPack.packSize));

  const addToCartDisabled = isPackageSizeInvalid || !selectedPack;
  const isSubmitEnabled = !addToCartDisabled;

  let okLabel;
  if (editMode) {
    okLabel = "quoteListPage.applyBtn";
  } else {
    if (isProductPMA(productDescriptor)) {
      okLabel = "cartPage.btn.quote";
    } else {
      okLabel = "addToCartPopup.add.submitBtn";
    }
  }
  okLabel = t(okLabel);

  const showAddToCart = userHasAnyPermission(Permission.ADD_TO_CART, Permission.ADD_TO_QUOTE);

  const hasSurcharge = watch("surcharge") > 0;

  const isSpecial = isSpecialPrice(productDescriptor, selectedPackCode);

  return (
    <Modal
      title={t("addToCartPopup.title")}
      size={isDesktop ? ModalSize.MEDIUM : ModalSize.TABLET}
      onClose={() => closePopup()}
      footerChildren={
        <Stack direction={"row"} gap={1}>
          {getCloseBtn(t("cancel"), !isTablet)}
          {showAddToCart && getActionBtn(okLabel, submit, !isSubmitEnabled)}
        </Stack>
      }
      {...getModalProps()}
    >
      <div className="scha__atc-popup__wrapper">
        <div className={classNames(getMainCls(), "scha__atc-popup-continer")}>
          <div className="scha__atc-popup--column-info">
            <AtcProductInfo descriptor={productDescriptor} />
          </div>
          <div className="scha__atc-popup--packaging-selection-notifications-wrapper">
            <div className="scha__atc-popup--column-packaging__wrapper scha__atc-popup--column-packaging scha__atc-kit-summary--column-packaging">
              <div className="scha__atc-popup--column-packaging">
                {hasIncludedParts && (
                  <IncludedPartsTable
                    title={t("modal.atc.housing.included.title")}
                    items={includedParts}
                    kitQuantity={quantity}
                  />
                )}
                {hasOptionalParts && (
                  <OptionalPartsTable
                    className={classNames(hasIncludedParts && "scha__atc-popup--optional-parts")}
                    title={t("modal.atc.housing.optional.title")}
                    items={optionalParts}
                  />
                )}
              </div>
              <AtcProductPriceInfo
                quantity={quantity}
                price={price}
                onChange={(newQty) => setValue("quantity", newQty, { shouldDirty: true, shouldTouch: true })}
                invalidQuantity={isPackageSizeInvalid ?? undefined}
                priceUnit={priceUnit}
                priceReference={priceReference}
                onPriceReferenceChange={(newVal) => setValue("priceReference", newVal ?? "")}
                descriptor={productDescriptor}
                hasSurcharge={hasSurcharge}
                isSpecialPrice={isSpecial}
                showInfoOrderHandlingUnitQty={true}
                disabled={false} // sending
              />
            </div>
            <Stack gap={1} className="notifications scha__atc-popup--notifications">
              <HousingMessages.EditQuantity className="w-100" />
              {isProductPMA(productDescriptor) && <InfoProductMessagePMA />}
              {userHasPermission(Permission.ADD_TO_CART) && (
                <Alert severity="info" className="w-100">
                  {t("addToCartPopup.disclaimer")}
                </Alert>
              )}
              {productDescriptor.base.isOptime && <OptimeSolutionsMessage />}
              {getCountriesListFromFifteendigit({ descriptor: productDescriptor }).length > 0 && (
                <DisclaimerCountryOrigin />
              )}
            </Stack>
          </div>
        </div>
      </div>
    </Modal>
  );
};

interface PartsTableProps {
  className?: string;
  title: ReactNode;
  items: HousingProduct[];
}

const IncludedPartsTable = ({
  className,
  title,
  items: parts,
  kitQuantity,
}: PartsTableProps & { kitQuantity: number }) => {
  const [displayAll, setDisplayAll] = useState(false);
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();

  const items: DataTableRow[] = useMemo(
    () =>
      parts.map((item) => ({
        quantity: item.quantity * kitQuantity,
        description: getDescription(item),
        productId: getProductId(item),
      })),
    [parts, kitQuantity],
  );

  return (
    <div className={classNames("scha__atc__parts-table", className)}>
      <p className="scha__atc__parts-table__title">{title}</p>
      <TanStackTable
        data={displayAll || isDesktop ? items : items.slice(0, 2)}
        columns={colConfig}
        fullwidth={true}
        variant={"clear"}
        className="scha__atc__kit_table"
      />
      {!displayAll && !isDesktop && (
        <LinkButton className={"mt-2"} onClick={() => setDisplayAll(true)}>
          {t("addToCartPopup.kit.expandAll.btn")}
        </LinkButton>
      )}
    </div>
  );
};

const OptionalPartsTable = ({ className, title, items: parts }: PartsTableProps) => {
  const [displayAll, setDisplayAll] = useState(false);
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();

  const formProps = useFormContext<AddToCartFormData>();
  const formItems = formProps.watch("optionalParts");

  const items = useMemo(
    () =>
      parts
        .map((part) => {
          const formItem = formItems[part.id];
          if (!formItem?.selected) {
            return null;
          }
          return {
            quantity: formItem.quantity,
            description: getDescription(part),
            productId: getProductId(part),
          };
        })
        .filter(isNonNull),
    [parts, formItems],
  );

  return (
    <div className={classNames("scha__atc__parts-table", className)}>
      <p className="scha__atc__parts-table__title">{title}</p>
      <TanStackTable
        data={displayAll || isDesktop ? items : items.slice(0, 2)}
        columns={colConfig}
        fullwidth={true}
        variant={"clear"}
        className="scha__atc__kit_table"
      />
      {!displayAll && !isDesktop && (
        <LinkButton className={"mt-2"} onClick={() => setDisplayAll(true)}>
          {t("addToCartPopup.kit.expandAll.btn")}
        </LinkButton>
      )}
    </div>
  );
};

interface DataTableRow {
  quantity: number;
  productId: JSX.Element;
  description: JSX.Element | string;
}

const colConfig: ColumnDef<DataTableRow, number>[] = [
  {
    accessorKey: "quantity",
    header: () => <Trans i18nKey={"input.quantity"} />,
    size: 0.2,
    cell: (cell) => cell.renderValue(),
  },
  {
    accessorKey: "productId",
    header: () => <Trans i18nKey={"bulkUpload.table.header.productId"} />,
    size: 0.4,
    cell: (cell) => cell.renderValue(),
  },
  {
    accessorKey: "description",
    header: () => <Trans i18nKey={"cartPage.report.description"} />,
    size: 0.4,
    cell: (cell) => cell.renderValue(),
  },
];

const getDescription = (item: HousingProduct) =>
  item.type === HousingProductType.HOUSING ? "-" : <Translation {...item.product.shortDescription} />;
const getProductId = (item: HousingProduct) => (
  <ProductDesignation
    designation={item.product.designation}
    isPreferred={item.product.preferred}
    reservePreferredSpace={false}
    size={"label-large"}
  />
);
